/**
 * Lumi core: cart, section fetching, events, money.
 * Every component in the theme talks to the store through this file.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});
  const routes = (Lumi.routes = Lumi.routes || { root: '/' });

  /* ---------- Events ---------- */
  Lumi.on = (name, handler) => document.addEventListener(name, handler);
  Lumi.off = (name, handler) => document.removeEventListener(name, handler);
  Lumi.emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ---------- Money ---------- */
  function withDelimiters(cents, precision, thousands, decimal) {
    const fixed = (cents / 100).toFixed(precision);
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  }

  Lumi.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    const fmt = format || Lumi.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
  };

  /* ---------- Sections ---------- */
  Lumi.fetchSection = async function (url, sectionId, options = {}) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const res = await fetch(target.toString(), { signal: options.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Section ${sectionId} failed with ${res.status}`);
    return res.text();
  };

  Lumi.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Cart ---------- */
  const DEFAULT_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const join = (path) => (routes.root.endsWith('/') ? routes.root.slice(0, -1) : routes.root) + path;

  async function post(url, body, source) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok || (data && data.status && data.status >= 400)) {
      const message = (data && (data.description || data.message)) || (Lumi.strings && Lumi.strings.cartError) || 'Something went wrong.';
      Lumi.emit('lumi:cart:error', { message, source });
      const err = new Error(message);
      err.data = data;
      throw err;
    }
    return data;
  }

  async function afterChange(data, sections) {
    const cart = await Lumi.cart.get();
    Lumi.emit('lumi:cart:updated', { cart, sections: (data && data.sections) || null, requested: sections });
    return data;
  }

  Lumi.cart = {
    async get() {
      const res = await fetch(join('/cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      return res.json();
    },
    async add(items, sections = DEFAULT_SECTIONS) {
      const data = await post(join('/cart/add.js'), {
        items,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }, 'add');
      return afterChange(data, sections);
    },
    async change(line, quantity, sections = DEFAULT_SECTIONS) {
      const data = await post(join('/cart/change.js'), {
        line,
        quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }, 'change');
      return afterChange(data, sections);
    },
    async update(updates, sections = DEFAULT_SECTIONS, extra = {}) {
      const data = await post(join('/cart/update.js'), {
        updates,
        ...extra,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }, 'update');
      return afterChange(data, sections);
    },
  };

  /* ---------- Section HTML swapping ---------- */
  // Replace the inner HTML of every [data-section-swap="<id>"] element with the
  // matching element from a Section Rendering API response.
  Lumi.swapSections = function (sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = Lumi.parseHTML(html);
      document.querySelectorAll(`[data-section-swap="${id}"]`).forEach((target) => {
        const fresh = doc.querySelector(`[data-section-swap="${id}"]`);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });
    });
  };

  Lumi.on('lumi:cart:updated', (e) => Lumi.swapSections(e.detail && e.detail.sections));

  /* ---------- Small helpers ---------- */
  Lumi.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  Lumi.announce = function (message) {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  Lumi.storage = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable: feature degrades to this page view only */
      }
    },
  };

  Lumi.reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Buttons get a soft light that follows the pointer.
  document.addEventListener('pointermove', (e) => {
    const btn = e.target.closest && e.target.closest('.btn');
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    btn.style.setProperty('--mx', `${e.clientX - r.left}px`);
    btn.style.setProperty('--my', `${e.clientY - r.top}px`);
  });

  // Lock page scroll while a modal surface is open.
  Lumi.lockScroll = (on) => document.documentElement.classList.toggle('is-locked', !!on);
})();
